import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, DollarSign } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/marketing/lib/utils";
import { Input } from "@/marketing/components/ui/input";
import type { CampaignInfluencer } from "@/marketing/models/campaignInfluencer";
import type {
  CampaignSettlement,
  UpsertCampaignSettlementRequest,
} from "@/marketing/models/campaignSettlement";
import type { CampaignPipelineDefinition } from "@/marketing/models/campaignPipeline";
import { getStageLabel } from "@/marketing/lib/campaignPipeline";
import { getStageToneStyle } from "@/marketing/lib/pipelineTone";
import { campaignSettlementAPIClient } from "@/marketing/infrastructure/influencer/campaign/settlement/campaignSettlementAPIClient";
import { CAMPAIGN_SETTLEMENT_KEYS } from "@/marketing/infrastructure/influencer/campaign/settlement/utils/keys";
import { CAMPAIGN_SETTLEMENT_QUERIES } from "@/marketing/infrastructure/influencer/campaign/settlement/utils/queries";
import { useAuthStore } from "@/marketing/stores/useAuthStore";
import { formatDate } from "@/marketing/lib/formatters";
import {
  COMMISSION_MINIMUM_PAYOUT_USD,
  isBelowCommissionPayoutMinimum,
} from "./commissionPayoutMinimum";
import { isSettlementRowEligible } from "./settlementRowEligibility";

interface SettlementTabProps {
  campaignId: string;
  pipelineDefinition: CampaignPipelineDefinition;
  influencers: CampaignInfluencer[];
}

interface SettlementRow {
  influencer: CampaignInfluencer;
  settlement: CampaignSettlement | null;
}

// Each PUT replaces the whole settlement, so every save carries the row's full current inputs.
interface SettlementDraft {
  settled: boolean;
  amount: string;
  note: string;
}

type SaveSettlementDraft = (draft: SettlementDraft, onFailed: () => void) => boolean;

function fmtUSD(
  amount: string | undefined,
  currency: string | undefined,
): string {
  if (!amount) return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  const ccy = (currency ?? "USD").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: ccy,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${ccy} ${n.toLocaleString()}`;
  }
}

function normalizeSettlementAmount(amount: string | undefined): string | undefined {
  const trimmed = amount?.trim();
  if (!trimmed) return undefined;
  const numericAmount = Number(trimmed);
  if (!Number.isFinite(numericAmount) || numericAmount < 0) return undefined;
  return trimmed;
}

function isValidSettlementAmount(amount: string): boolean {
  const trimmed = amount.trim();
  if (!trimmed) return true;
  const numericAmount = Number(trimmed);
  return Number.isFinite(numericAmount) && numericAmount >= 0;
}

function isSameSettlementDraft(a: SettlementDraft, b: SettlementDraft): boolean {
  return (
    a.settled === b.settled &&
    normalizeSettlementAmount(a.amount) === normalizeSettlementAmount(b.amount) &&
    a.note.trim() === b.note.trim()
  );
}

function draftPayoutFields(
  settlement: CampaignSettlement | null,
  amount: string,
): Pick<CampaignSettlement, "kind" | "amount"> {
  return { ...(settlement?.kind !== undefined && { kind: settlement.kind }), amount };
}

function sanitizeSettlementAmountInput(nextValue: string, currentValue: string): string {
  if (nextValue === "") return nextValue;
  if (!/^\d*(?:\.\d*)?$/.test(nextValue)) return currentValue;
  return nextValue;
}

export function SettlementTab({
  campaignId,
  pipelineDefinition,
  influencers,
}: SettlementTabProps) {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const settledBy = currentUser?.email ?? "unknown";

  const settlementsQuery = useQuery(
    CAMPAIGN_SETTLEMENT_QUERIES.list(campaignId),
  );

  const upsertMutation = useMutation({
    mutationFn: ({
      influencerId,
      request,
    }: {
      influencerId: string;
      request: UpsertCampaignSettlementRequest;
    }) =>
      campaignSettlementAPIClient.upsertCampaignInfluencerSettlement(
        campaignId,
        influencerId,
        request,
      ),
    // Saves for one campaign run in order so a slower earlier PUT cannot land last.
    scope: { id: `campaign-settlement:${campaignId}` },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: CAMPAIGN_SETTLEMENT_KEYS.lists(),
      });
    },
    onError: () => toast.error("Failed to update settlement"),
  });

  const rows: SettlementRow[] = useMemo(() => {
    const settlementById = new Map<string, CampaignSettlement>();
    (settlementsQuery.data ?? []).forEach((s) =>
      settlementById.set(s.influencerId, s),
    );
    return influencers
      .filter((c) => isSettlementRowEligible(c, settlementById.has(c.influencerId), pipelineDefinition))
      .map((influencer) => ({
        influencer,
        settlement: settlementById.get(influencer.influencerId) ?? null,
      }));
  }, [influencers, pipelineDefinition, settlementsQuery.data]);

  const summary = useMemo(() => {
    let totalCommitted = 0;
    let paidOut = 0;
    let pending = 0;
    let withAmount = 0;
    for (const row of rows) {
      const amount = row.settlement?.amount ? Number(row.settlement.amount) : 0;
      if (Number.isFinite(amount) && amount > 0) {
        totalCommitted += amount;
        withAmount += 1;
        if (row.settlement?.settled) paidOut += amount;
        else pending += amount;
      }
    }
    const avg = withAmount > 0 ? Math.round(totalCommitted / withAmount) : 0;
    return { totalCommitted, paidOut, pending, avg };
  }, [rows]);

  const saveSettlement = (row: SettlementRow, draft: SettlementDraft, onFailed: () => void): boolean => {
    if (!isValidSettlementAmount(draft.amount)) {
      toast.error("Settlement amount must be 0 or greater");
      return false;
    }
    const amount = normalizeSettlementAmount(draft.amount);
    const currency = row.settlement?.currency ?? (amount !== undefined ? "USD" : undefined);
    const externalRefNote = draft.note.trim();
    // mutate-level callbacks only fire for the latest call, so each queued save restores through its own promise.
    void upsertMutation.mutateAsync({
      influencerId: row.influencer.influencerId,
      request: {
        settled: draft.settled,
        settledBy,
        ...(amount !== undefined && { amount }),
        ...(currency !== undefined && { currency }),
        ...(externalRefNote !== "" && { externalRefNote }),
      },
    }).catch(onFailed);
    return true;
  };

  const togglePaid = (row: SettlementRow, draft: SettlementDraft, onFailed: () => void): boolean => {
    if (draft.settled && isBelowCommissionPayoutMinimum(draftPayoutFields(row.settlement, draft.amount))) {
      toast.error(
        `Commission payouts start at $${COMMISSION_MINIMUM_PAYOUT_USD}. Leave the row open and it carries over to the next cycle.`,
      );
      return false;
    }
    return saveSettlement(row, draft, onFailed);
  };

  return (
    <div className="tw-px-8 tw-py-6">
      {/* 4-stat summary */}
      <div className="tw-mb-4 tw-grid tw-grid-cols-2 tw-gap-3 lg:tw-grid-cols-4">
        <SettleStat
          label="Total committed"
          value={fmtUSD(String(summary.totalCommitted), "USD")}
        />
        <SettleStat
          label="Paid out"
          value={fmtUSD(String(summary.paidOut), "USD")}
          tone="emerald"
        />
        <SettleStat
          label="Pending"
          value={fmtUSD(String(summary.pending), "USD")}
          tone="amber"
        />
        <SettleStat
          label="Avg per influencer"
          value={fmtUSD(String(summary.avg), "USD")}
          sub={`${rows.length} eligible`}
        />
      </div>

      <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-border">
        <table className="tw-w-full tw-text-sm">
          <thead className="tw-bg-muted/50">
            <tr className="tw-text-left tw-text-[11px] tw-uppercase tw-tracking-wider tw-text-muted-foreground">
              <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Influencer</th>
              <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Stage</th>
              <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Amount</th>
              <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Receipt / notes</th>
              <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Date paid</th>
              <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Paid?</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="tw-px-3 tw-py-8 tw-text-center tw-text-sm tw-text-muted-foreground"
                >
                  No published influencers yet — settlement rows appear once a
                  creator reaches Published.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <SettlementRowView
                  pipelineDefinition={pipelineDefinition}
                  key={row.influencer.influencerId}
                  row={row}
                  onTogglePaid={(draft, onFailed) => togglePaid(row, draft, onFailed)}
                  onSave={(draft, onFailed) => saveSettlement(row, draft, onFailed)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {settlementsQuery.isError && (
        <p className="tw-mt-2 tw-text-xs tw-text-red-600">
          Failed to load settlements. Inline edits will retry on next change.
        </p>
      )}
    </div>
  );
}

function SettleStat({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "default" | "emerald" | "amber";
}) {
  return (
    <div className="tw-rounded-lg tw-border tw-border-border tw-bg-card tw-p-4">
      <div className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "tw-mt-1 tw-text-xl tw-font-semibold tw-tabular-nums",
          tone === "emerald" && "tw-text-emerald-700",
          tone === "amber" && "tw-text-amber-700",
        )}
      >
        {value}
      </div>
      {sub && (
        <div className="tw-mt-1 tw-text-[11px] tw-text-muted-foreground">{sub}</div>
      )}
    </div>
  );
}

interface SettlementRowViewProps {
  pipelineDefinition: CampaignPipelineDefinition;
  row: SettlementRow;
  onTogglePaid: SaveSettlementDraft;
  onSave: SaveSettlementDraft;
}

function SettlementRowView({
  pipelineDefinition,
  row,
  onTogglePaid,
  onSave,
}: SettlementRowViewProps) {
  const { influencer, settlement } = row;
  const [amount, setAmount] = useState(
    normalizeSettlementAmount(settlement?.amount) ?? "",
  );
  const [note, setNote] = useState(settlement?.externalRefNote ?? "");
  const [settled, setSettled] = useState(settlement?.settled ?? false);
  const lastSent = useRef<SettlementDraft>({ settled, amount, note });
  const latestSettlement = useRef(settlement);
  latestSettlement.current = settlement;

  // One effect per field so a refetched amount never overwrites a note still being typed.
  useEffect(() => {
    const serverAmount = normalizeSettlementAmount(settlement?.amount) ?? "";
    setAmount(serverAmount);
    lastSent.current = { ...lastSent.current, amount: serverAmount };
  }, [settlement?.amount]);

  useEffect(() => {
    const serverNote = settlement?.externalRefNote ?? "";
    setNote(serverNote);
    lastSent.current = { ...lastSent.current, note: serverNote };
  }, [settlement?.externalRefNote]);

  useEffect(() => {
    const serverSettled = settlement?.settled ?? false;
    setSettled(serverSettled);
    lastSent.current = { ...lastSent.current, settled: serverSettled };
  }, [settlement?.settled]);

  // A failed save puts the row back on the server's state; the typed inputs stay so a re-blur retries.
  const restoreServerState = () => {
    const server = latestSettlement.current;
    const serverSettled = server?.settled ?? false;
    lastSent.current = {
      settled: serverSettled,
      amount: normalizeSettlementAmount(server?.amount) ?? "",
      note: server?.externalRefNote ?? "",
    };
    setSettled(serverSettled);
  };

  const send = (next: SettlementDraft, save: SaveSettlementDraft) => {
    if (isSameSettlementDraft(next, lastSent.current)) return;
    if (!save(next, restoreServerState)) return;
    lastSent.current = next;
    setSettled(next.settled);
  };

  const belowPayoutMinimum = !settled && isBelowCommissionPayoutMinimum(draftPayoutFields(settlement, amount));
  const handle = influencer.handle ?? influencer.influencerId.slice(0, 8);
  const toneStyle = getStageToneStyle(
    pipelineDefinition,
    influencer.pipelineStage,
  );

  return (
    <tr className="tw-border-t tw-border-border">
      <td className="tw-px-3 tw-py-2.5">
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-muted tw-text-[10px] tw-font-semibold tw-text-foreground/70">
            {handle
              .replace(/[^a-zA-Z]/g, "")
              .slice(0, 2)
              .toUpperCase() || "??"}
          </div>
          <div className="tw-min-w-0">
            <div className="tw-truncate tw-font-medium">@{handle}</div>
            <div className="tw-truncate tw-text-[11px] tw-text-muted-foreground">
              {influencer.influencerId}
            </div>
          </div>
        </div>
      </td>
      <td className="tw-px-3 tw-py-2.5">
        <span
          className={cn(
            "tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold",
            toneStyle.pill,
          )}
        >
          <span className={cn("tw-h-1.5 tw-w-1.5 tw-rounded-full", toneStyle.dot)} />
          {getStageLabel(pipelineDefinition, influencer.pipelineStage)}
        </span>
      </td>
      <td className="tw-px-3 tw-py-2.5">
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <DollarSign className="tw-h-3 tw-w-3 tw-text-muted-foreground" />
          <Input
            value={amount}
            onChange={(e) =>
              setAmount((currentAmount) =>
                sanitizeSettlementAmountInput(e.target.value, currentAmount),
              )
            }
            onBlur={() => send({ settled, amount, note }, onSave)}
            placeholder="0.00"
            className="tw-h-8 tw-w-24 tw-text-sm tw-tabular-nums"
            inputMode="decimal"
          />
          <span className="tw-text-[11px] tw-text-muted-foreground">
            {settlement?.currency ?? "USD"}
          </span>
        </div>
      </td>
      <td className="tw-px-3 tw-py-2.5">
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => send({ settled, amount, note }, onSave)}
          placeholder="wise.com/r/…"
          className="tw-h-8 tw-text-sm"
        />
      </td>
      <td className="tw-px-3 tw-py-2.5 tw-text-[12px] tw-text-muted-foreground">
        {settlement?.settledAt ? formatDate(settlement.settledAt) : "—"}
      </td>
      <td className="tw-px-3 tw-py-2.5">
        <button
          type="button"
          onClick={() => send({ settled: !settled, amount, note }, onTogglePaid)}
          disabled={belowPayoutMinimum}
          title={
            belowPayoutMinimum
              ? `Commission payouts start at $${COMMISSION_MINIMUM_PAYOUT_USD}`
              : undefined
          }
          className={cn(
            "tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium",
            settled
              ? "tw-border-emerald-200 tw-bg-emerald-50 tw-text-emerald-800"
              : "tw-border-border tw-bg-background tw-text-foreground hover:tw-bg-muted",
            belowPayoutMinimum && "tw-cursor-not-allowed tw-opacity-50 hover:tw-bg-background",
          )}
        >
          <span
            className={cn(
              "tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded tw-border",
              settled
                ? "tw-border-emerald-500 tw-bg-emerald-500 tw-text-white"
                : "tw-border-border tw-bg-background",
            )}
          >
            {settled && <Check className="tw-h-3 tw-w-3" />}
          </span>
          {settled ? "Paid" : "Mark paid"}
        </button>
        {belowPayoutMinimum && (
          <div className="tw-mt-1 tw-text-[11px] tw-text-amber-700">
            Below ${COMMISSION_MINIMUM_PAYOUT_USD} minimum — carries over
          </div>
        )}
      </td>
    </tr>
  );
}
